import React, { useState } from 'react';
import { Row, RemoveX, Btn, TextInput } from './host/kit';

/**
 * LinksEditor — add/remove URL + display-text rows for an event or Signal.
 *
 * Stores `[{url, text}]`. Caller owns the array via `value` + `onChange`.
 * Cap at 10 rows (matches backend SerializeLinks). The display-text is
 * optional — if omitted, the rendered pill uses a short hostname.
 *
 * `theme` lets the caller swap styles: 'default' (brutalist white-bg),
 * 'portal' (Portal Noir's dark register) or 'pill' — the host-event wizard's
 * new Portal UI (docs/plans/2026-09-host-event-edit, Step 4 EXTRAS · LINKS):
 * grey pill rows with a black label pill (a missing label reads LINK), the URL
 * with an ellipsis and a white ✕; a wrapping add row. Storage is identical.
 */
export default function LinksEditor({ value = [], onChange, max = 10, theme = 'default' }) {
  const links = Array.isArray(value) ? value : [];
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');

  const add = () => {
    const trimmed = url.trim();
    if (!trimmed || links.length >= max) return;
    onChange([...links, { url: trimmed, text: text.trim() || null }]);
    setUrl('');
    setText('');
  };

  const remove = (i) => {
    const next = links.slice();
    next.splice(i, 1);
    onChange(next);
  };

  if (theme === 'pill') {
    const atCap = links.length >= max;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {links.map((l, i) => (
          <Row key={`${i}-${l.url}`} grey round pad="6">
            <span
              style={{ flexShrink: 0, maxWidth: '50%', height: 32, padding: '0 14px', borderRadius: 999, background: '#000', color: '#fff', display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {(l.text || 'LINK').toUpperCase()}
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={l.url}>
              {l.url}
            </span>
            <RemoveX white large label="Remove link" onClick={() => remove(i)} />
          </Row>
        ))}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <TextInput
            size="sm"
            type="url"
            inputMode="url"
            placeholder="https://example.com"
            aria-label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={atCap}
            style={{ flex: '2 1 220px', width: 'auto' }}
          />
          <TextInput
            size="sm"
            type="text"
            placeholder="e.g. Menu, Tickets, Map"
            aria-label="Label (optional)"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 80))}
            disabled={atCap}
            style={{ flex: '1 1 160px', width: 'auto' }}
          />
          <Btn onClick={add} disabled={!url.trim() || atCap}>
            {atCap ? `MAX ${max} REACHED` : '+ ADD LINK'}
          </Btn>
        </div>
      </div>
    );
  }

  const isPortal = theme === 'portal';
  const labelCls = isPortal ? 'portal-label' : 'block font-mono text-[10px] uppercase tracking-wider mb-1';
  const inputCls = isPortal ? 'portal-input' : 'w-full border-2 border-black px-3 py-2 font-mono text-xs';
  const pillCls = isPortal ? 'portal-chip active' : 'inline-flex items-center gap-2 border-2 border-black bg-white text-black px-2 py-1 font-mono text-xs uppercase tracking-wider';
  const removeBtnCls = isPortal ? 'portal-mono' : 'font-mono text-xs';
  const addBtnCls = isPortal ? 'portal-btn solid' : 'border-2 border-black bg-black text-white px-4 py-2 font-mono text-xs uppercase tracking-wider disabled:opacity-50';

  return (
    <div>
      {links.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {links.map((l, i) => (
            <span key={`${i}-${l.url}`} className={pillCls} style={{ gap: 8 }}>
              <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(l.text || stripScheme(l.url)).toUpperCase()}
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                className={removeBtnCls}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 'bold' }}
                aria-label="Remove link"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label className={labelCls}>URL</label>
          <input
            type="url"
            inputMode="url"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className={inputCls}
            disabled={links.length >= max}
          />
        </div>
        <div>
          <label className={labelCls}>Label (optional)</label>
          <input
            type="text"
            placeholder="e.g. Menu, Tickets, Map"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 80))}
            className={inputCls}
            disabled={links.length >= max}
          />
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <button
          type="button"
          onClick={add}
          disabled={!url.trim() || links.length >= max}
          className={addBtnCls}
        >
          {links.length >= max ? `MAX ${max} REACHED` : 'ADD LINK +'}
        </button>
      </div>
    </div>
  );
}

function stripScheme(url) {
  return (url || '').replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
}

/**
 * LinksPills — read-only renderer used on event/signal detail pages.
 */
export function LinksPills({ links, theme = 'default', className }) {
  if (!Array.isArray(links) || links.length === 0) return null;
  const isPortal = theme === 'portal';
  const pillCls = isPortal
    ? 'portal-chip'
    : 'inline-block border-2 border-black bg-white text-black px-3 py-1 font-mono text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors';
  return (
    <div className={className} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {links.map((l, i) => (
        <a
          key={`${i}-${l.url}`}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          className={pillCls}
          style={{ textDecoration: 'none' }}
        >
          {(l.text || stripScheme(l.url)).toUpperCase()}
        </a>
      ))}
    </div>
  );
}
